import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Empty,
  Input,
  List,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message
} from 'antd';
import { VendorLookupPane } from './Vendors/components/VendorLookupPane';
import { SalesforceLookupPane } from './Vendors/components/SalesforceLookupPane';
import { ReconciliationActionsCard } from './Vendors/components/ReconciliationActionsCard';
import { VendorManagementModals } from './Vendors/components/VendorManagementModals';
import { VendorMigrationSection } from './Vendors/components/VendorMigrationSection';
import {
  SalesforceSupplierSearchResult,
  VendorMigrationProductResolution,
  VendorMigrationResolutionAction,
  useVendorApi
} from 'sb/api/vendor';
import { useRecoilValue } from 'recoil';
import { uiState } from 'recoil/atoms';
import { animateScroll } from 'react-scroll';
import { scrollOptions } from 'sb/shared/constants';
import { useReconciliationSearch } from './Vendors/hooks/useReconciliationSearch';
import { useVendorLinking } from './Vendors/hooks/useVendorLinking';
import { useVendorMigration } from './Vendors/hooks/useVendorMigration';
import { useVendorSelection } from './Vendors/hooks/useVendorSelection';
import {
  buildSharedVisibleSalesforceIds,
  DOMAIN_ONLY_PATTERN,
  getMigrationErrorMessage,
  getMigrationErrorStatus,
  getSalesforceTagColor,
  normalizeWebsiteDomain,
  PANE_HEADER_FALLBACK_CLASS,
  PANE_HEADER_LOGO_CLASS,
  toVendorFormValues
} from './Vendors/vendorsPage.utils';
import {
  CreateRecordDraft,
  CreateSalesforceAccountDraft,
  RenameSalesforceAccountDraft
} from './Vendors/vendorsPage.types';

const thisMenuKey = 'supplier';

const Vendors: React.FC = () => {
  const [form] = Form.useForm();
  const [createRecordForm] = Form.useForm<CreateRecordDraft>();
  const [createSalesforceAccountForm] = Form.useForm<CreateSalesforceAccountDraft>();
  const [renameSalesforceAccountForm] = Form.useForm<RenameSalesforceAccountDraft>();

  // LOCAL STATE
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [isDeleteVendorModalOpen, setIsDeleteVendorModalOpen] = useState(false);
  const [isCreateRecordModalOpen, setIsCreateRecordModalOpen] = useState(false);
  const [isCreateSalesforceAccountModalOpen, setIsCreateSalesforceAccountModalOpen] = useState(false);
  const [isRenameSalesforceAccountModalOpen, setIsRenameSalesforceAccountModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // RECOIL STATE
  const { menuKey } = useRecoilValue(uiState);

  // API
  const {
    getVendorDetails,
    getVendorBySalesforceId,
    updateVendor,
    createVendor,
    searchVendorLookup,
    searchSalesforceSuppliers,
    setVendorSalesforceLink,
    deleteVendor,
    createSalesforceSupplierAccount,
    renameSalesforceSupplierAccount,
    previewVendorMigration,
    executeVendorMigration,
    searchMigrationTargetProducts
  } = useVendorApi();

  const {
    updating,
    addOrEditVendor,
    vendor,
    selectedVendor,
    setVendor,
    setSelectedVendor,
    clearSelectedVendorContext,
    refreshSelectedVendorDetails,
    handleVendorChange,
    handleSubmit,
    handleEdit,
    handleCancel
  } = useVendorSelection({
    form,
    getVendorDetails,
    updateVendor,
    createVendor
  });

  const {
    selectedSalesforceSupplierId,
    selectedSalesforceSupplier,
    salesforceAccountLinkedVendorLookupLoading,
    salesforceAccountLinkedVendor,
    isMissingSelection,
    vendorLinkedToDifferentAccount,
    salesforceAccountLinkedToDifferentVendor,
    selectedRecordsMatchBySalesforceId,
    requiresOverride,
    setSelectedSalesforceSupplierId,
    setSelectedSalesforceSupplier,
    setSalesforceAccountLinkedVendorLookupLoading,
    setSalesforceAccountLinkedVendor,
    fetchLinkedVendorForSalesforceAccount,
    clearSelectedSalesforceContext,
    handleSalesforceSelection
  } = useVendorLinking({
    vendor,
    getVendorBySalesforceId
  });

  const {
    migrationTargetSearchTerm,
    migrationTargetSearchLoading,
    migrationTargetSearchResults,
    selectedMigrationTargetVendor,
    migrationPreviewLoading,
    migrationExecuteLoading,
    migrationPreview,
    migrationExecuteResult,
    migrationResolutionsByProductId,
    migrationManualSearchTermsByProductId,
    migrationManualSearchLoadingByProductId,
    migrationManualSearchResultsByProductId,
    isExecuteMigrationModalOpen,
    isAutoMatchedProductsExpanded,
    setMigrationTargetSearchTerm,
    setMigrationTargetSearchLoading,
    setMigrationTargetSearchResults,
    setSelectedMigrationTargetVendor,
    setMigrationPreviewLoading,
    setMigrationExecuteLoading,
    setMigrationPreview,
    setMigrationExecuteResult,
    setMigrationResolutionsByProductId,
    setMigrationManualSearchTermsByProductId,
    setMigrationManualSearchLoadingByProductId,
    setMigrationManualSearchResultsByProductId,
    setIsExecuteMigrationModalOpen,
    setIsAutoMatchedProductsExpanded,
    handleManualTargetProductTypeahead
  } = useVendorMigration({
    sourceVendorId: vendor?.id,
    searchMigrationTargetProducts
  });

  const {
    searchesLocked,
    lockedSearchTerm,
    vendorSearchTerm,
    salesforceSearchTerm,
    vendorLookupSearchLoading,
    vendorLookupResults,
    salesforceSupplierSearchLoading,
    salesforceSupplierResults,
    effectiveSalesforceSearchTerm,
    setSalesforceSearchTerm,
    setSalesforceSupplierResults,
    setSalesforceSupplierSearchLoading,
    handleSearchLockToggle,
    handleLockedSearchChange,
    handleUnlockedVendorSearchChange,
    refreshVantagePane
  } = useReconciliationSearch({
    searchVendorLookup,
    searchSalesforceSuppliers,
    onLockedSearchCleared: () => {
      clearSelectedVendorContext();
      clearSelectedSalesforceContext();
    },
    onVendorSearchCleared: clearSelectedVendorContext
  });

  const refreshSalesforcePane = async (options?: {
    preserveSelectedId?: string;
    fallbackSelection?: SalesforceSupplierSearchResult;
  }) => {
    const preserveSelectedId = options?.preserveSelectedId ?? selectedSalesforceSupplierId;
    const trimmedSearchTerm = effectiveSalesforceSearchTerm.trim();

    if (trimmedSearchTerm.length >= 2) {
      setSalesforceSupplierSearchLoading(true);
      try {
        const refreshedResults = await searchSalesforceSuppliers(trimmedSearchTerm);
        setSalesforceSupplierResults(refreshedResults);

        if (preserveSelectedId) {
          const refreshedSelection = refreshedResults.find((item) => item.Id === preserveSelectedId);
          if (refreshedSelection) {
            setSelectedSalesforceSupplierId(refreshedSelection.Id);
            setSelectedSalesforceSupplier(refreshedSelection);
          } else if (options?.fallbackSelection) {
            const fallback = options.fallbackSelection;
            setSelectedSalesforceSupplierId(fallback.Id);
            setSelectedSalesforceSupplier(fallback);
            setSalesforceSupplierResults((previousResults) => {
              const withoutFallback = previousResults.filter((item) => item.Id !== fallback.Id);
              return [fallback, ...withoutFallback];
            });
          }
        }
      } finally {
        setSalesforceSupplierSearchLoading(false);
      }
    } else if (options?.fallbackSelection) {
      const fallback = options.fallbackSelection;
      setSelectedSalesforceSupplierId(fallback.Id);
      setSelectedSalesforceSupplier(fallback);
      setSalesforceSupplierResults((previousResults) => {
        const withoutFallback = previousResults.filter((item) => item.Id !== fallback.Id);
        return [fallback, ...withoutFallback];
      });
    }

    if (preserveSelectedId) {
      setSalesforceAccountLinkedVendorLookupLoading(true);
      const linkedVendor = await fetchLinkedVendorForSalesforceAccount(preserveSelectedId);
      setSalesforceAccountLinkedVendor(linkedVendor);
      setSalesforceAccountLinkedVendorLookupLoading(false);
    } else {
      setSalesforceAccountLinkedVendor(null);
      setSalesforceAccountLinkedVendorLookupLoading(false);
    }
  };

  useEffect(() => {
    if (menuKey === thisMenuKey) {
      animateScroll.scrollToTop(scrollOptions);
    }
  }, [menuKey]);

  const sharedVisibleSalesforceIds = useMemo(
    () => buildSharedVisibleSalesforceIds(vendorLookupResults, salesforceSupplierResults),
    [vendorLookupResults, salesforceSupplierResults]
  );
  const selectedVendorLookupItem = vendorLookupResults.find((item) => item.id === vendor?.id);
  const selectedVendorDealCount = selectedVendorLookupItem?.deal_count;
  const canDeleteVendor = Boolean(vendor && !vendor.salesforce_id && selectedVendorDealCount === 0);
  const actionModeLabel = requiresOverride ? 'Override required' : 'Normal link';
  let linkStatusLabel = 'Ready to link';
  if (isMissingSelection) {
    linkStatusLabel = 'Select one vendor and one Salesforce account';
  } else if (vendor?.salesforce_id === selectedSalesforceSupplier?.Id) {
    linkStatusLabel = 'Already linked';
  } else if (requiresOverride) {
    linkStatusLabel = 'Conflict detected';
  }
  const linkStatusTagColor =
    linkStatusLabel === 'Already linked' ? 'green' : linkStatusLabel === 'Conflict detected' ? 'red' : 'default';

  const handleLinkSelectedRecords = async () => {
    if (!vendor || !selectedSalesforceSupplier) {
      message.warning('Select one Vantage vendor and one Salesforce account first.');
      return;
    }

    if (requiresOverride) {
      setIsOverrideModalOpen(true);
      return;
    }

    setIsActionLoading(true);
    try {
      await setVendorSalesforceLink(vendor.id, selectedSalesforceSupplier.Id, false);
      await refreshSelectedVendorDetails(vendor.id);
      await refreshSalesforcePane({
        preserveSelectedId: selectedSalesforceSupplier.Id,
        fallbackSelection: selectedSalesforceSupplier
      });
      await refreshVantagePane();
      message.success('Vendor linked to Salesforce account.');
    } catch {
      message.error('Unable to link records. If the account is already linked elsewhere, use override.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleConfirmOverride = async () => {
    if (!vendor || !selectedSalesforceSupplier) {
      setIsOverrideModalOpen(false);
      message.warning('Select one Vantage vendor and one Salesforce account first.');
      return;
    }

    setIsActionLoading(true);
    try {
      await setVendorSalesforceLink(vendor.id, selectedSalesforceSupplier.Id, true);
      setIsOverrideModalOpen(false);
      await refreshSelectedVendorDetails(vendor.id);
      await refreshSalesforcePane({
        preserveSelectedId: selectedSalesforceSupplier.Id,
        fallbackSelection: selectedSalesforceSupplier
      });
      await refreshVantagePane();
      message.success('Salesforce account link was overridden successfully.');
    } catch {
      message.error('Unable to override link ownership.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!vendor) {
      setIsDeleteVendorModalOpen(false);
      message.warning('Select a Vantage vendor before deleting.');
      return;
    }

    setIsActionLoading(true);
    try {
      await deleteVendor(vendor.id);
      setIsDeleteVendorModalOpen(false);
      clearSelectedVendorContext();
      await refreshVantagePane();
      await refreshSalesforcePane({
        preserveSelectedId: selectedSalesforceSupplierId,
        fallbackSelection: selectedSalesforceSupplier
      });
      message.success('Vantage vendor deleted.');
    } catch {
      message.error(
        'Unable to delete vendor. Deletion is only allowed when Salesforce ID is empty and no linked deals or competitor records exist.'
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleVendorSearchChange = (value: string) => {
    if (value.trim()) {
      clearSelectedSalesforceContext();
    }
    handleUnlockedVendorSearchChange(value);
  };

  const handleMigrationTargetSearch = async (rawValue?: string) => {
    const searchTerm = (rawValue ?? migrationTargetSearchTerm).trim();
    if (searchTerm.length < 2) {
      setMigrationTargetSearchResults([]);
      return;
    }

    setMigrationTargetSearchLoading(true);
    try {
      const results = await searchVendorLookup(searchTerm);
      const filteredResults = results.filter((item) => item.id !== vendor?.id);
      setMigrationTargetSearchResults(filteredResults);
    } catch {
      setMigrationTargetSearchResults([]);
      message.error('Unable to search target vendors.');
    } finally {
      setMigrationTargetSearchLoading(false);
    }
  };

  const handleMigrationPreview = async () => {
    if (!vendor) {
      message.warning('Select a source vendor before previewing migration.');
      return;
    }
    if (!selectedMigrationTargetVendor) {
      message.warning('Select a target vendor before previewing migration.');
      return;
    }
    if (vendor.id === selectedMigrationTargetVendor.id) {
      message.warning('Source and target vendors must be different.');
      return;
    }

    setMigrationPreviewLoading(true);
    try {
      const preview = await previewVendorMigration({
        source_vendor_id: vendor.id,
        target_vendor_id: selectedMigrationTargetVendor.id
      });
      setMigrationPreview(preview);
      setMigrationExecuteResult(null);
      setMigrationResolutionsByProductId({});
      setMigrationManualSearchTermsByProductId({});
      setMigrationManualSearchLoadingByProductId({});
      setMigrationManualSearchResultsByProductId({});
      setIsAutoMatchedProductsExpanded(false);
      message.success('Migration preview generated.');
    } catch (error) {
      const status = getMigrationErrorStatus(error);
      if (status === 404) {
        message.error('Source or target vendor no longer exists.');
      } else if (status === 403) {
        message.error('You do not have permission to run vendor migrations.');
      } else if (status === 400) {
        message.error(getMigrationErrorMessage(error) || 'Invalid migration preview request.');
      } else {
        message.error('Unable to generate migration preview.');
      }
    } finally {
      setMigrationPreviewLoading(false);
    }
  };

  const upsertMigrationResolution = (
    sourceProductId: number,
    action: VendorMigrationResolutionAction,
    targetProductId?: number
  ) => {
    setMigrationResolutionsByProductId((previousResolutions) => ({
      ...previousResolutions,
      [sourceProductId]: {
        source_product_id: sourceProductId,
        action,
        ...(targetProductId ? { target_product_id: targetProductId } : {})
      }
    }));
  };

  const handleExecuteVendorMigration = async () => {
    if (!vendor || !selectedMigrationTargetVendor || !migrationPreview) {
      setIsExecuteMigrationModalOpen(false);
      message.warning('Create a migration preview before executing.');
      return;
    }

    const unresolvedProducts = migrationPreview.unresolved_products;
    const resolutions = unresolvedProducts
      .map((unresolvedProduct) => migrationResolutionsByProductId[unresolvedProduct.source_product.id])
      .filter((resolution): resolution is VendorMigrationProductResolution => Boolean(resolution));

    if (resolutions.length !== unresolvedProducts.length) {
      setIsExecuteMigrationModalOpen(false);
      message.warning('Resolve all unresolved products before executing migration.');
      return;
    }

    setMigrationExecuteLoading(true);
    try {
      const executeResult = await executeVendorMigration({
        source_vendor_id: vendor.id,
        target_vendor_id: selectedMigrationTargetVendor.id,
        preview_hash: migrationPreview.preview_hash,
        ...(resolutions.length ? { resolutions } : {})
      });
      setMigrationExecuteResult(executeResult);
      setMigrationPreview(null);
      setMigrationResolutionsByProductId({});
      setMigrationManualSearchTermsByProductId({});
      setMigrationManualSearchLoadingByProductId({});
      setMigrationManualSearchResultsByProductId({});
      setIsExecuteMigrationModalOpen(false);

      await refreshVantagePane();
      const refreshedVendor = await getVendorDetails(vendor.id);
      if (refreshedVendor) {
        setVendor(refreshedVendor);
        setSelectedVendor(refreshedVendor.id);
        form.setFieldsValue(toVendorFormValues(refreshedVendor));
      } else {
        clearSelectedVendorContext();
      }

      message.success('Vendor migration executed successfully.');
    } catch (error) {
      const status = getMigrationErrorStatus(error);
      if (status === 409) {
        setIsExecuteMigrationModalOpen(false);
        message.warning('Migration preview is stale. Regenerate preview and try again.');
      } else if (status === 403) {
        message.error('You do not have permission to execute vendor migration.');
      } else if (status === 400) {
        message.error(getMigrationErrorMessage(error) || 'Invalid migration execute request.');
      } else {
        message.error('Unable to execute vendor migration.');
      }
    } finally {
      setMigrationExecuteLoading(false);
    }
  };

  const openCreateRecordModal = () => {
    createRecordForm.setFieldsValue({
      mode: vendor ? 'both' : 'vendor',
      name: vendor?.name || selectedSalesforceSupplier?.Name || '',
      domain: normalizeWebsiteDomain(vendor?.domain || selectedSalesforceSupplier?.Website)
    });
    setIsCreateRecordModalOpen(true);
  };

  const handleCreateRecord = async () => {
    let createDraft: CreateRecordDraft;
    try {
      createDraft = await createRecordForm.validateFields();
    } catch {
      return;
    }

    const mode = createDraft.mode;
    const normalizedName = createDraft.name?.trim();
    const normalizedDomain = normalizeWebsiteDomain(createDraft.domain);

    if (!normalizedName) {
      message.warning('Name is required.');
      return;
    }

    setIsActionLoading(true);
    let createdVendorId: number | undefined;
    let createdSalesforceAccountId: string | undefined;
    let createdSalesforceSelection: SalesforceSupplierSearchResult | undefined;

    try {
      if (mode === 'vendor' || mode === 'both') {
        const createdVendor = await createVendor({
          name: normalizedName,
          ...(normalizedDomain ? { domain: normalizedDomain } : {})
        });
        createdVendorId = createdVendor?.id;
      }

      if (mode === 'salesforce' || mode === 'both') {
        const createResponse = await createSalesforceSupplierAccount({
          Name: normalizedName,
          ...(normalizedDomain ? { Website: normalizedDomain } : {})
        });
        if (!createResponse.success) {
          throw new Error(createResponse.errors.join(', ') || 'Salesforce rejected account creation.');
        }
        createdSalesforceAccountId = createResponse.id;
        createdSalesforceSelection = {
          Id: createResponse.id,
          Name: normalizedName,
          Website: normalizedDomain ?? null,
          Deals_as_Primary_Vendor_Last_24_Mos_RH__c: null
        };
      }

      if (mode === 'both' && createdVendorId && createdSalesforceAccountId) {
        await setVendorSalesforceLink(createdVendorId, createdSalesforceAccountId, false);
      }

      setIsCreateRecordModalOpen(false);

      if (createdVendorId) {
        await refreshVantagePane();
        await refreshSelectedVendorDetails(createdVendorId);
      }

      if (createdSalesforceSelection) {
        setSelectedSalesforceSupplierId(createdSalesforceSelection.Id);
        setSelectedSalesforceSupplier(createdSalesforceSelection);
        await refreshSalesforcePane({
          preserveSelectedId: createdSalesforceSelection.Id,
          fallbackSelection: createdSalesforceSelection
        });
      }

      if (mode === 'both') {
        message.success('Vendor and Salesforce account created and linked.');
      } else if (mode === 'vendor') {
        message.success('Vendor created successfully.');
      } else {
        message.success('Salesforce account created successfully.');
      }
    } catch {
      if (mode === 'both' && createdVendorId && !createdSalesforceAccountId) {
        await refreshVantagePane();
        await refreshSelectedVendorDetails(createdVendorId);
        message.warning('Vendor created, but Salesforce account creation failed.');
      } else if (mode === 'both' && createdVendorId && createdSalesforceAccountId) {
        const fallbackSelection: SalesforceSupplierSearchResult = {
          Id: createdSalesforceAccountId,
          Name: normalizedName,
          Website: normalizedDomain ?? null,
          Deals_as_Primary_Vendor_Last_24_Mos_RH__c: null
        };
        await refreshVantagePane();
        await refreshSelectedVendorDetails(createdVendorId);
        await refreshSalesforcePane({
          preserveSelectedId: createdSalesforceAccountId,
          fallbackSelection
        });
        message.warning('Vendor and Salesforce account were created, but linking failed.');
      } else {
        message.error('Unable to create record.');
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const openCreateSalesforceAccountModal = () => {
    if (!vendor) {
      message.warning('Select a Vantage vendor before creating a Salesforce account.');
      return;
    }

    createSalesforceAccountForm.setFieldsValue({
      Name: vendor.name ?? '',
      Website: normalizeWebsiteDomain(vendor.domain),
      Is_Vendor__c: true
    });
    setIsCreateSalesforceAccountModalOpen(true);
  };

  const handleCreateSalesforceAccount = async () => {
    if (!vendor) {
      message.warning('Select a Vantage vendor before creating a Salesforce account.');
      return;
    }

    let createDraft: CreateSalesforceAccountDraft;
    try {
      createDraft = await createSalesforceAccountForm.validateFields();
    } catch {
      return;
    }

    const normalizedWebsite = normalizeWebsiteDomain(createDraft.Website);

    setIsActionLoading(true);
    try {
      const createResponse = await createSalesforceSupplierAccount({
        Name: createDraft.Name,
        ...(normalizedWebsite ? { Website: normalizedWebsite } : {})
      });
      if (!createResponse.success) {
        throw new Error(createResponse.errors.join(', ') || 'Salesforce rejected account creation.');
      }

      await setVendorSalesforceLink(vendor.id, createResponse.id, false);

      const createdSelection: SalesforceSupplierSearchResult = {
        Id: createResponse.id,
        Name: createDraft.Name,
        Website: normalizedWebsite ?? null,
        Deals_as_Primary_Vendor_Last_24_Mos_RH__c: null
      };

      setSelectedSalesforceSupplierId(createdSelection.Id);
      setSelectedSalesforceSupplier(createdSelection);
      setIsCreateSalesforceAccountModalOpen(false);
      await refreshSelectedVendorDetails(vendor.id);
      await refreshSalesforcePane({
        preserveSelectedId: createdSelection.Id,
        fallbackSelection: createdSelection
      });
      message.success('Salesforce account created and linked to the selected vendor.');
    } catch {
      message.error('Unable to create Salesforce account.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const openRenameSalesforceAccountModal = () => {
    if (!selectedSalesforceSupplier) {
      message.warning('Select a Salesforce supplier account before renaming.');
      return;
    }

    renameSalesforceAccountForm.setFieldsValue({
      Name: selectedSalesforceSupplier.Name
    });
    setIsRenameSalesforceAccountModalOpen(true);
  };

  const handleRenameSalesforceAccount = async () => {
    if (!selectedSalesforceSupplier) {
      message.warning('Select a Salesforce supplier account before renaming.');
      return;
    }

    let renameDraft: RenameSalesforceAccountDraft;
    try {
      renameDraft = await renameSalesforceAccountForm.validateFields();
    } catch {
      return;
    }

    setIsActionLoading(true);
    try {
      const renameResponse = await renameSalesforceSupplierAccount(selectedSalesforceSupplier.Id, renameDraft.Name);
      if (!renameResponse.success) {
        throw new Error(renameResponse.errors.join(', ') || 'Salesforce rejected account rename.');
      }

      const renamedSelection: SalesforceSupplierSearchResult = {
        ...selectedSalesforceSupplier,
        Name: renameDraft.Name
      };

      setSelectedSalesforceSupplier(renamedSelection);
      setIsRenameSalesforceAccountModalOpen(false);
      await refreshSalesforcePane({
        preserveSelectedId: selectedSalesforceSupplier.Id,
        fallbackSelection: renamedSelection
      });

      if (vendor?.salesforce_id === selectedSalesforceSupplier.Id) {
        await refreshSelectedVendorDetails(vendor.id);
      }

      message.success('Salesforce account name updated.');
    } catch {
      message.error('Unable to rename Salesforce account.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const migrationUnresolvedProducts = migrationPreview?.unresolved_products ?? [];
  const migrationCanExecute = Boolean(
    vendor &&
      selectedMigrationTargetVendor &&
      migrationPreview &&
      migrationUnresolvedProducts.every((unresolvedProduct) => {
        const resolution = migrationResolutionsByProductId[unresolvedProduct.source_product.id];
        if (!resolution) {
          return false;
        }
        if (resolution.action === VendorMigrationResolutionAction.MapToTargetProduct && !resolution.target_product_id) {
          return false;
        }
        return !(
          resolution.action === VendorMigrationResolutionAction.MoveSourceProductToTargetVendor &&
          resolution.target_product_id
        );
      })
  );

  return (
    <>
      <h1 className="mb-2">Suppliers</h1>
      <Card className="pt-4">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col flex="auto">
            <Typography.Title level={5} className="!mb-0">
              Reconciliation Search
            </Typography.Title>
          </Col>
          <Col>
            <Space>
              <Typography.Text>Searches locked</Typography.Text>
              <Switch checked={searchesLocked} onChange={handleSearchLockToggle} />
            </Space>
          </Col>
        </Row>

        <div className="mt-4">
          {searchesLocked ? (
            <Input.Search
              value={lockedSearchTerm}
              allowClear
              size="large"
              placeholder="Search both systems by vendor name, domain, or Salesforce ID"
              onChange={(event) => handleLockedSearchChange(event.target.value)}
            />
          ) : (
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Input.Search
                  value={vendorSearchTerm}
                  allowClear
                  size="large"
                  placeholder="Search Vantage vendors"
                  onChange={(event) => handleVendorSearchChange(event.target.value)}
                />
              </Col>
              <Col span={12}>
                <Input.Search
                  value={salesforceSearchTerm}
                  allowClear
                  size="large"
                  placeholder="Search Salesforce supplier accounts"
                  onChange={(event) => setSalesforceSearchTerm(event.target.value)}
                />
              </Col>
            </Row>
          )}
        </div>

        <Row className="mt-5" gutter={[16, 16]} align="top">
          <VendorLookupPane
            vendorLookupSearchLoading={vendorLookupSearchLoading}
            vendorLookupResults={vendorLookupResults}
            selectedVendor={selectedVendor}
            selectedRecordsMatchBySalesforceId={selectedRecordsMatchBySalesforceId}
            selectedSalesforceSupplier={selectedSalesforceSupplier}
            handleVendorChange={handleVendorChange}
            getSalesforceTagColor={(salesforceId) => getSalesforceTagColor(sharedVisibleSalesforceIds, salesforceId)}
            vendor={vendor}
            updating={updating}
            form={form}
            addOrEditVendor={addOrEditVendor}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            handleEdit={handleEdit}
            canDeleteVendor={canDeleteVendor}
            isActionLoading={isActionLoading}
            openDeleteModal={() => setIsDeleteVendorModalOpen(true)}
            paneHeaderLogoClass={PANE_HEADER_LOGO_CLASS}
            paneHeaderFallbackClass={PANE_HEADER_FALLBACK_CLASS}
          />
          <SalesforceLookupPane
            salesforceSupplierSearchLoading={salesforceSupplierSearchLoading}
            salesforceSupplierResults={salesforceSupplierResults}
            selectedSalesforceSupplierId={selectedSalesforceSupplierId}
            selectedRecordsMatchBySalesforceId={selectedRecordsMatchBySalesforceId}
            vendor={vendor}
            handleSalesforceSelection={handleSalesforceSelection}
            getSalesforceTagColor={(salesforceId) => getSalesforceTagColor(sharedVisibleSalesforceIds, salesforceId)}
            selectedSalesforceSupplier={selectedSalesforceSupplier}
            isActionLoading={isActionLoading}
            openRenameSalesforceAccountModal={openRenameSalesforceAccountModal}
            paneHeaderLogoClass={PANE_HEADER_LOGO_CLASS}
            paneHeaderFallbackClass={PANE_HEADER_FALLBACK_CLASS}
          />
        </Row>

        <ReconciliationActionsCard
          requiresOverride={requiresOverride}
          actionModeLabel={actionModeLabel}
          linkStatusTagColor={linkStatusTagColor}
          linkStatusLabel={linkStatusLabel}
          vendor={vendor}
          selectedSalesforceSupplier={selectedSalesforceSupplier}
          isMissingSelection={isMissingSelection}
          vendorLinkedToDifferentAccount={vendorLinkedToDifferentAccount}
          salesforceAccountLinkedToDifferentVendor={salesforceAccountLinkedToDifferentVendor}
          salesforceAccountLinkedVendorLookupLoading={salesforceAccountLinkedVendorLookupLoading}
          salesforceAccountLinkedVendorId={salesforceAccountLinkedVendor?.id}
          salesforceAccountLinkedVendorName={salesforceAccountLinkedVendor?.name}
          isActionLoading={isActionLoading}
          onLinkSelectedRecords={handleLinkSelectedRecords}
          onOpenCreateRecordModal={openCreateRecordModal}
          onOpenOverrideModal={() => setIsOverrideModalOpen(true)}
          onOpenCreateSalesforceAccountModal={openCreateSalesforceAccountModal}
        />

        <div className="hidden">
          <VendorMigrationSection>
            <div className="mb-4 rounded border border-gray-200 bg-white p-4">
              <Typography.Title level={5} className="!mb-3">
                Migration Setup
              </Typography.Title>
              <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
                Source vendor:{' '}
                {vendor ? (
                  <span className="font-medium">
                    #{vendor.id} - {vendor.name}
                  </span>
                ) : (
                  'None selected'
                )}
              </Typography.Paragraph>
              <Typography.Paragraph className="!mb-3 text-sm text-gray-700">
                Target vendor:{' '}
                {selectedMigrationTargetVendor ? (
                  <span className="font-medium">
                    #{selectedMigrationTargetVendor.id} - {selectedMigrationTargetVendor.name}
                  </span>
                ) : (
                  'None selected'
                )}
              </Typography.Paragraph>
              <Input.Search
                value={migrationTargetSearchTerm}
                allowClear
                placeholder="Search target vendor by name, domain, or Salesforce ID"
                onChange={(event) => setMigrationTargetSearchTerm(event.target.value)}
                onSearch={handleMigrationTargetSearch}
              />
              <div className="mt-3 max-h-52 overflow-y-auto rounded border border-gray-200">
                {migrationTargetSearchLoading ? (
                  <div className="flex justify-center py-8">
                    <Spin />
                  </div>
                ) : migrationTargetSearchResults.length > 0 ? (
                  <List
                    dataSource={migrationTargetSearchResults}
                    renderItem={(targetVendor) => {
                      const isSelected = selectedMigrationTargetVendor?.id === targetVendor.id;
                      return (
                        <List.Item
                          className={`cursor-pointer px-4 py-2 ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          } border-b border-gray-100`}
                          onClick={() => setSelectedMigrationTargetVendor(targetVendor)}
                        >
                          <div className="w-full">
                            <Typography.Text strong>{targetVendor.name}</Typography.Text>
                            <div className="text-xs text-gray-500">
                              #{targetVendor.id} | Domain: {targetVendor.domain || 'N/A'} | SFDC ID:{' '}
                              {targetVendor.salesforce_id || 'N/A'}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Empty className="my-3" image={Empty.PRESENTED_IMAGE_SIMPLE} description="No target vendors" />
                )}
              </div>
              <Space className="mt-3">
                <Button type="primary" onClick={handleMigrationPreview} loading={migrationPreviewLoading}>
                  Generate migration preview
                </Button>
                <Button
                  onClick={() => {
                    setMigrationTargetSearchTerm('');
                    setMigrationTargetSearchResults([]);
                    setSelectedMigrationTargetVendor(undefined);
                  }}
                  disabled={!selectedMigrationTargetVendor && migrationTargetSearchResults.length === 0}
                >
                  Clear target selection
                </Button>
              </Space>
            </div>

            {migrationPreview && (
              <>
                <div className="mb-4 rounded border border-gray-200 bg-white p-4">
                  <Typography.Title level={5} className="!mb-3">
                    Migration Preview
                  </Typography.Title>
                  <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
                    Preview hash: <span className="font-medium">{migrationPreview.preview_hash}</span>
                  </Typography.Paragraph>
                  <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
                    Deals: {migrationPreview.summary.deal_count} | Source deal parties:{' '}
                    {migrationPreview.summary.source_deal_party_count} | Line items:{' '}
                    {migrationPreview.summary.line_item_count} | Linked contract transactions:{' '}
                    {migrationPreview.summary.linked_contract_transaction_count}
                  </Typography.Paragraph>
                  <Typography.Paragraph className="!mb-0 mt-1 text-sm text-gray-700">
                    Auto matches: {migrationPreview.summary.auto_match_count} | Unresolved products:{' '}
                    {migrationPreview.summary.unresolved_product_count}
                  </Typography.Paragraph>
                </div>

                {migrationPreview.warnings.length > 0 && (
                  <div className="mb-4">
                    {migrationPreview.warnings.map((warning, index) => (
                      <Alert key={`${warning}-${index}`} className="mb-2" type="warning" showIcon message={warning} />
                    ))}
                  </div>
                )}

                <div className="mb-4 rounded border border-gray-200 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Typography.Title level={5} className="!mb-0">
                      Auto-Matched Products
                    </Typography.Title>
                    <Button
                      type="link"
                      size="small"
                      aria-expanded={isAutoMatchedProductsExpanded}
                      onClick={() => setIsAutoMatchedProductsExpanded((isExpanded) => !isExpanded)}
                    >
                      {isAutoMatchedProductsExpanded ? 'Hide auto-matched products' : 'Show auto-matched products'}
                    </Button>
                  </div>
                  {isAutoMatchedProductsExpanded &&
                    (migrationPreview.auto_matches.length > 0 ? (
                      <List
                        dataSource={migrationPreview.auto_matches}
                        renderItem={(autoMatch) => (
                          <List.Item className="px-0">
                            <div className="w-full">
                              <div className="flex items-center gap-2">
                                <Typography.Text strong>
                                  {autoMatch.source_product.code || 'No code'} - {autoMatch.source_product.name}
                                </Typography.Text>
                                <Tag color="blue">{autoMatch.strategy}</Tag>
                              </div>
                              <Typography.Paragraph className="!mb-1 mt-1 text-sm text-gray-700">
                                Target: #{autoMatch.target_product.id} {autoMatch.target_product.code || 'No code'} -{' '}
                                {autoMatch.target_product.name}
                              </Typography.Paragraph>
                              <Typography.Paragraph className="!mb-0 text-xs text-gray-500">
                                Affected line items: {autoMatch.affected_line_item_count} | Affected deals:{' '}
                                {autoMatch.affected_deal_count}
                              </Typography.Paragraph>
                            </div>
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No auto-matched products" />
                    ))}
                  {!isAutoMatchedProductsExpanded && (
                    <Typography.Paragraph className="!mb-0 text-sm text-gray-500">
                      Expand to review {migrationPreview.summary.auto_match_count} auto-matched product
                      {migrationPreview.summary.auto_match_count === 1 ? '' : 's'}.
                    </Typography.Paragraph>
                  )}
                </div>

                <div className="mb-4 rounded border border-gray-200 bg-white p-4">
                  <Typography.Title level={5} className="!mb-3">
                    Unresolved Products
                  </Typography.Title>
                  {migrationUnresolvedProducts.length > 0 ? (
                    <List
                      dataSource={migrationUnresolvedProducts}
                      renderItem={(unresolvedProduct) => {
                        const sourceProductId = unresolvedProduct.source_product.id;
                        const resolution = migrationResolutionsByProductId[sourceProductId];
                        const manualSearchResults = migrationManualSearchResultsByProductId[sourceProductId] || [];
                        return (
                          <List.Item className="px-0">
                            <div className="w-full rounded border border-gray-200 p-3">
                              <Typography.Text strong>
                                #{sourceProductId} {unresolvedProduct.source_product.code || 'No code'} -{' '}
                                {unresolvedProduct.source_product.name}
                              </Typography.Text>
                              <Typography.Paragraph className="!mb-1 mt-1 text-sm text-gray-700">
                                Reason: {unresolvedProduct.reason}
                              </Typography.Paragraph>
                              <Typography.Paragraph className="!mb-2 text-xs text-gray-500">
                                Affected line items: {unresolvedProduct.affected_line_item_count} | Affected deals:{' '}
                                {unresolvedProduct.affected_deal_count}
                              </Typography.Paragraph>

                              <Space wrap className="mb-2">
                                {unresolvedProduct.candidate_target_products.map((candidateProduct) => (
                                  <Button
                                    key={candidateProduct.id}
                                    size="small"
                                    onClick={() =>
                                      upsertMigrationResolution(
                                        sourceProductId,
                                        VendorMigrationResolutionAction.MapToTargetProduct,
                                        candidateProduct.id
                                      )
                                    }
                                  >
                                    Map to #{candidateProduct.id} {candidateProduct.code || 'No code'}
                                  </Button>
                                ))}
                                <Button
                                  size="small"
                                  onClick={() =>
                                    upsertMigrationResolution(
                                      sourceProductId,
                                      VendorMigrationResolutionAction.MoveSourceProductToTargetVendor
                                    )
                                  }
                                >
                                  Move source product to target vendor
                                </Button>
                              </Space>

                              <Select
                                showSearch
                                className="mb-2 w-full"
                                placeholder="Search target products for manual mapping"
                                filterOption={false}
                                searchValue={migrationManualSearchTermsByProductId[sourceProductId] || ''}
                                onSearch={(value) => handleManualTargetProductTypeahead(sourceProductId, value)}
                                onClear={() => {
                                  handleManualTargetProductTypeahead(sourceProductId, '');
                                }}
                                allowClear
                                loading={Boolean(migrationManualSearchLoadingByProductId[sourceProductId])}
                                options={manualSearchResults.map((candidateProduct) => ({
                                  value: candidateProduct.id,
                                  label: `#${candidateProduct.id} ${candidateProduct.code || 'No code'} - ${
                                    candidateProduct.name
                                  }`
                                }))}
                                onSelect={(targetProductId) =>
                                  upsertMigrationResolution(
                                    sourceProductId,
                                    VendorMigrationResolutionAction.MapToTargetProduct,
                                    Number(targetProductId)
                                  )
                                }
                                notFoundContent={
                                  migrationManualSearchLoadingByProductId[sourceProductId] ? (
                                    <Spin size="small" />
                                  ) : null
                                }
                              />

                              {resolution && (
                                <Alert
                                  type="info"
                                  showIcon
                                  message={
                                    resolution.action === VendorMigrationResolutionAction.MapToTargetProduct
                                      ? `Resolution: map to target product #${resolution.target_product_id}`
                                      : 'Resolution: move source product to target vendor'
                                  }
                                />
                              )}
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No unresolved products" />
                  )}
                </div>
              </>
            )}

            <div className="rounded border border-gray-200 bg-white p-4">
              <Typography.Title level={5} className="!mb-3">
                Execute Migration
              </Typography.Title>
              <Typography.Paragraph className="!mb-2 text-sm text-gray-700">
                Execute is enabled only after preview is generated and all unresolved products have a selected
                resolution.
              </Typography.Paragraph>
              <Button
                type="primary"
                danger
                disabled={!migrationCanExecute}
                loading={migrationExecuteLoading}
                onClick={() => setIsExecuteMigrationModalOpen(true)}
              >
                Execute vendor migration
              </Button>
            </div>

            {migrationExecuteResult && (
              <div className="mt-4 rounded border border-gray-200 bg-white p-4">
                <Typography.Title level={5} className="!mb-3">
                  Last Execution Summary
                </Typography.Title>
                <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
                  Migrated deals: {migrationExecuteResult.summary.migrated_deal_count} | In-place deal_party updates:{' '}
                  {migrationExecuteResult.summary.in_place_deal_party_updates} | Merged deal_party rows:{' '}
                  {migrationExecuteResult.summary.merged_deal_party_count}
                </Typography.Paragraph>
                <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
                  Rewired deal proposals: {migrationExecuteResult.summary.rewired_deal_proposal_count} | Rewired
                  documents: {migrationExecuteResult.summary.rewired_document_count}
                </Typography.Paragraph>
                <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
                  Remapped line items: {migrationExecuteResult.summary.remapped_line_item_count} | Remapped contract
                  transactions: {migrationExecuteResult.summary.remapped_contract_transaction_count}
                </Typography.Paragraph>
                <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
                  Moved source products: {migrationExecuteResult.summary.moved_product_count}
                </Typography.Paragraph>
                {migrationExecuteResult.warnings.length > 0 && (
                  <div className="mt-3">
                    {migrationExecuteResult.warnings.map((warning, index) => (
                      <Alert key={`${warning}-${index}`} className="mb-2" type="warning" showIcon message={warning} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </VendorMigrationSection>
        </div>
      </Card>

      <VendorManagementModals
        isOverrideModalOpen={isOverrideModalOpen}
        isDeleteVendorModalOpen={isDeleteVendorModalOpen}
        isCreateRecordModalOpen={isCreateRecordModalOpen}
        isCreateSalesforceAccountModalOpen={isCreateSalesforceAccountModalOpen}
        isRenameSalesforceAccountModalOpen={isRenameSalesforceAccountModalOpen}
        isExecuteMigrationModalOpen={isExecuteMigrationModalOpen}
        isActionLoading={isActionLoading}
        migrationExecuteLoading={migrationExecuteLoading}
        vendor={vendor}
        selectedSalesforceSupplier={selectedSalesforceSupplier}
        selectedMigrationTargetVendor={selectedMigrationTargetVendor}
        migrationPreview={migrationPreview}
        createRecordForm={createRecordForm}
        createSalesforceAccountForm={createSalesforceAccountForm}
        renameSalesforceAccountForm={renameSalesforceAccountForm}
        domainOnlyPattern={DOMAIN_ONLY_PATTERN}
        onCloseOverrideModal={() => setIsOverrideModalOpen(false)}
        onCloseDeleteVendorModal={() => setIsDeleteVendorModalOpen(false)}
        onCloseCreateRecordModal={() => setIsCreateRecordModalOpen(false)}
        onCloseCreateSalesforceAccountModal={() => setIsCreateSalesforceAccountModalOpen(false)}
        onCloseRenameSalesforceAccountModal={() => setIsRenameSalesforceAccountModalOpen(false)}
        onCloseExecuteMigrationModal={() => setIsExecuteMigrationModalOpen(false)}
        onConfirmOverride={handleConfirmOverride}
        onDeleteVendor={handleDeleteVendor}
        onCreateRecord={handleCreateRecord}
        onCreateSalesforceAccount={handleCreateSalesforceAccount}
        onRenameSalesforceAccount={handleRenameSalesforceAccount}
        onExecuteVendorMigration={handleExecuteVendorMigration}
      />
    </>
  );
};

export default Vendors;
